import { useState } from "react";
import {
  parseIntegrationRequest,
  integrationDataSourceSchema,
  uid,
  type IntegrationDataSource,
} from "@proof/core";
import { Button, Control } from "./components";
export function IntegrationDataSources({
  value,
  onChange,
}: {
  value: IntegrationDataSource[];
  onChange: (value: IntegrationDataSource[]) => void;
}) {
  const [request, setRequest] = useState(""),
    [notice, setNotice] = useState("");
  const patch = (index: number, update: Partial<IntegrationDataSource>) =>
    onChange(
      value.map((source, i) =>
        i === index ? { ...source, ...update } : source,
      ),
    );
  return (
    <section aria-label="External data permissions">
      <h3>External data permissions</h3>
      <p>
        Each source has its own fields, record ceiling, and permitted lookup
        parameters. Your backend issues a narrower record/field grant for each
        form session. Forms can combine these sources without a project.
      </p>
      <Control label="Import URL or cURL">
        <textarea
          value={request}
          onChange={(event) => setRequest(event.target.value)}
        />
      </Control>
      <Button
        disabled={!request.trim()}
        onClick={() => {
          try {
            const { warnings, ...parsed } = parseIntegrationRequest(request);
            onChange([
              ...value,
              integrationDataSourceSchema.parse({
                ...parsed,
                id: uid("source"),
                name: new URL(parsed.endpoint).hostname,
                mode: "http",
                fields: [{ key: "value", label: "Response value", path: "" }],
              }),
            ]);
            setRequest("");
            setNotice(
              [
                ...warnings,
                "Review allowed fields and the response mapping before saving.",
              ].join(" "),
            );
          } catch (error) {
            setNotice((error as Error).message);
          }
        }}
      >
        Import data source
      </Button>
      {notice && <p role="status">{notice}</p>}
      {value.map((source, index) => (
        <fieldset key={index} className="panel padded">
          <legend>{source.name || "Data source"}</legend>
          <div className="control-grid">
            <Control label="Source ID">
              <input
                value={source.id}
                onChange={(e) => patch(index, { id: e.target.value })}
              />
            </Control>
            <Control label="Source name">
              <input
                value={source.name}
                onChange={(e) => patch(index, { name: e.target.value })}
              />
            </Control>
            <Control label="Data delivery">
              <select
                value={source.mode}
                onChange={(e) => {
                  const next = {
                    ...source,
                    mode: e.target.value as "host" | "http",
                  };
                  if (next.mode === "host") delete next.endpoint;
                  else next.endpoint = "";
                  patch(index, next);
                }}
              >
                <option value="host">
                  Backend supplies data when issuing token
                </option>
                <option value="http">
                  Proof Studio queries an external API
                </option>
              </select>
            </Control>
            <Control label="Record permission">
              <select
                value={source.recordIds === null ? "any" : "listed"}
                onChange={(e) =>
                  patch(index, {
                    recordIds: e.target.value === "any" ? null : [],
                  })
                }
              >
                <option value="any">
                  Backend chooses records for each token
                </option>
                <option value="listed">Only these record IDs</option>
              </select>
            </Control>
          </div>
          {source.recordIds !== null && (
            <Control label="Allowed record IDs">
              <textarea
                value={source.recordIds.join("\n")}
                placeholder="One record ID per line"
                onChange={(e) =>
                  patch(index, {
                    recordIds: e.target.value.split(/\r?\n/),
                  })
                }
              />
            </Control>
          )}
          <Control label="Allowed lookup parameter keys">
            <input
              value={source.parameterKeys.join(",")}
              placeholder="region, language"
              onChange={(e) =>
                patch(index, {
                  parameterKeys: e.target.value.split(","),
                })
              }
            />
          </Control>
          {source.mode === "http" && (
            <div className="control-grid">
              <Control label="Request style">
                <select
                  value={
                    source.requestStyle === "parameters"
                      ? source.httpMethod || "POST"
                      : "envelope"
                  }
                  onChange={(event) =>
                    patch(
                      index,
                      event.target.value === "envelope"
                        ? { requestStyle: "envelope", httpMethod: "POST" }
                        : {
                            requestStyle: "parameters",
                            httpMethod: event.target.value as "GET" | "POST",
                          },
                    )
                  }
                >
                  <option value="envelope">Proof Studio POST envelope</option>
                  <option value="GET">REST GET parameters</option>
                  <option value="POST">REST JSON POST parameters</option>
                </select>
              </Control>
              <Control label="External HTTPS endpoint">
                <input
                  type="url"
                  value={source.endpoint || ""}
                  onChange={(e) => patch(index, { endpoint: e.target.value })}
                />
              </Control>
              <Control label="Credential header name">
                <input
                  value={source.authHeader}
                  onChange={(e) => patch(index, { authHeader: e.target.value })}
                />
              </Control>
              <p>
                Proof Studio sends a bounded data query to this fixed public
                HTTPS endpoint. Save credentials separately after creating the
                client. Redirects and private network destinations are blocked.
              </p>
            </div>
          )}
          <h4>Allowed fields</h4>
          {source.fields.map((field, fieldIndex) => (
            <div key={fieldIndex} className="panel padded">
              <div className="control-grid">
                <Control label="Data field key">
                  <input
                    value={field.key}
                    onChange={(e) =>
                      patch(index, {
                        fields: source.fields.map((item, i) =>
                          i === fieldIndex
                            ? { ...item, key: e.target.value }
                            : item,
                        ),
                      })
                    }
                  />
                </Control>
                <Control label="Data field label">
                  <input
                    value={field.label}
                    onChange={(e) =>
                      patch(index, {
                        fields: source.fields.map((item, i) =>
                          i === fieldIndex
                            ? { ...item, label: e.target.value }
                            : item,
                        ),
                      })
                    }
                  />
                </Control>
                <Control label="Field data type">
                  <select
                    value={field.mode}
                    onChange={(e) =>
                      patch(index, {
                        fields: source.fields.map((item, i) =>
                          i === fieldIndex
                            ? {
                                ...item,
                                mode: e.target.value as "value" | "choices",
                              }
                            : item,
                        ),
                      })
                    }
                  >
                    <option value="value">Field value</option>
                    <option value="choices">Dropdown / choice catalog</option>
                  </select>
                </Control>
                {source.mode === "http" && (
                  <>
                    <Control label="Response JSON pointer">
                      <input
                        value={field.path}
                        placeholder="/customer/name"
                        onChange={(e) =>
                          patch(index, {
                            fields: source.fields.map((item, i) =>
                              i === fieldIndex
                                ? { ...item, path: e.target.value }
                                : item,
                            ),
                          })
                        }
                      />
                    </Control>
                    {field.mode === "choices" && (
                      <>
                        <Control label="Choice value pointer">
                          <input
                            value={field.valuePath}
                            onChange={(e) =>
                              patch(index, {
                                fields: source.fields.map((item, i) =>
                                  i === fieldIndex
                                    ? { ...item, valuePath: e.target.value }
                                    : item,
                                ),
                              })
                            }
                          />
                        </Control>
                        <Control label="Choice label pointer">
                          <input
                            value={field.labelPath}
                            onChange={(e) =>
                              patch(index, {
                                fields: source.fields.map((item, i) =>
                                  i === fieldIndex
                                    ? { ...item, labelPath: e.target.value }
                                    : item,
                                ),
                              })
                            }
                          />
                        </Control>
                        <Control label="Selected value pointer (optional)">
                          <input
                            value={field.selectedPath || ""}
                            placeholder="/customer/selectedService"
                            onChange={(e) =>
                              patch(index, {
                                fields: source.fields.map((item, i) =>
                                  i === fieldIndex
                                    ? {
                                        ...item,
                                        selectedPath:
                                          e.target.value || undefined,
                                      }
                                    : item,
                                ),
                              })
                            }
                          />
                        </Control>
                      </>
                    )}
                  </>
                )}
              </div>
              <Button
                onClick={() =>
                  patch(index, {
                    fields: source.fields.filter((_, i) => i !== fieldIndex),
                  })
                }
              >
                Remove data field
              </Button>
            </div>
          ))}
          <div className="button-row">
            <Button
              disabled={source.fields.length >= 100}
              onClick={() =>
                patch(index, {
                  fields: [
                    ...source.fields,
                    {
                      key: `field${source.fields.length + 1}`,
                      label: "New field",
                      mode: "value",
                      path: "",
                      valuePath: "/id",
                      labelPath: "/name",
                    },
                  ],
                })
              }
            >
              Add data field
            </Button>
            <Button
              onClick={() => onChange(value.filter((_, i) => i !== index))}
            >
              Remove data source
            </Button>
          </div>
        </fieldset>
      ))}
      <Button
        disabled={value.length >= 20}
        onClick={() =>
          onChange([
            ...value,
            {
              id: `source${value.length + 1}`,
              name: "New source",
              mode: "host",
              recordIds: null,
              parameterKeys: [],
              authHeader: "Authorization",
              fields: [
                {
                  key: "name",
                  label: "Name",
                  mode: "value",
                  path: "/name",
                  valuePath: "/id",
                  labelPath: "/name",
                },
              ],
            },
          ])
        }
      >
        Add data source
      </Button>
    </section>
  );
}
